import Vue from 'vue'
import Vuex from 'vuex'
import Authenticator from '@/lib/Authenticator'

Vue.use(Vuex)

const auth = new Authenticator()

const state = {
  authenticated: !!localStorage.getItem('access_token'),
  accessToken: localStorage.getItem('access_token'),
  idToken: localStorage.getItem('id_token'),
  expiresAt: localStorage.getItem('expires_at'),
  profile: {},
  animalSources: [],
  bullSales: [],
  currentBullSale: {},
  appSettings: {},
  saleDateAccounts: []
}

const store = new Vuex.Store({
  state,
  getters: {
    authenticated (state) {
      return state.authenticated
    },
    profile: (state) => {
      return state.profile
    },
    animalSources: (state) => {
      return state.animalSources
    },
    bullSales: (state) => {
      return state.bullSales;
    },
    currentBullSale: (state) => {
      return state.currentBullSale
    },
    appSettings: (state) => {
      return state.appSettings
    },
    saleDateAccounts: (state) => {
      return state.saleDateAccounts
    },
    backgroundImage: () => {
      return 'https://cattlytics.blob.core.windows.net/assets/background-image.jpg'
    }
  },
  mutations: {
    authenticated (state, authData) {
      state.authenticated = true
      state.accessToken = authData.accessToken
      state.idToken = authData.idToken
      state.expiresAt = authData.expiresIn * 1000 + new Date().getTime()

      localStorage.setItem('access_token', state.accessToken)
      localStorage.setItem('id_token', state.idToken)
      localStorage.setItem('expires_at', state.expiresAt)
      
      store.dispatch('loadProfile', { authData: authData })
    },
    storeAnimalSources(state, animalSources) {
      state.animalSources = animalSources
      localStorage.setItem('animalSources', JSON.stringify(animalSources))
    },
    storeBullSales(state, sales) {
      state.bullSales = sales
      localStorage.setItem('bullSales', JSON.stringify(sales))
    },
    storeCurrentBullSale(state, sale) {
      state.currentBullSale = sale
      localStorage.setItem('currentBullSale', state.currentBullSale)
    },
    storeSaleDateAccounts(state, saleDateAccounts) {
      state.saleDateAccounts = saleDateAccounts
      localStorage.setItem('saleDateAccounts', JSON.stringify(saleDateAccounts))
    },
    logout (state) {
      state.authenticated = false
      state.accessToken = null
      state.idToken = false
      state.profile = {}
      state.animalSources = []
      state.bullSales = []
      state.currentBullSale = {}
      state.appSettings = {}
      state.saleDateAccounts = []

      localStorage.removeItem('access_token')
      localStorage.removeItem('id_token')
      localStorage.removeItem('expires_at')
      localStorage.removeItem('animalSources')
      localStorage.removeItem('bullSales')
      localStorage.removeItem('currentBullSale')
      localStorage.removeItem('appSettings')
      localStorage.removeItem('saleDateAccounts')

      auth.logOut()
    }
  },
  actions: {
    login (state) {
      auth.login(state)
    },

    logout ({ commit }) {
      commit('logout')
    },

    handleAuthentication ({ commit }) {
      auth.handleAuthentication().then(authResult => {
        commit('authenticated', authResult)
      })
    },

    updateAuthData ({ commit }, authResult) {
      commit('authenticated', authResult)
    },

    updateProfile (context, { profile }) {
      context.state.profile.name = profile.name
      context.state.profile.nickname = profile.nickname
      context.state.profile.picture = profile.picture
      context.state.profile.companyName = profile.companyName
      context.state.profile.phone1 = profile.phone1
      context.state.profile.phone2 = profile.phone2
      context.state.profile.cowHerdSize = profile.cowHerdSize
      context.state.profile.bullPreferences = profile.bullPreferences
      context.state.profile.isBeefboosterCustomer = profile.isBeefboosterCustomer
      context.state.profile.isHerdtraxCustomer = profile.isHerdtraxCustomer
    },

    updateHerdProfileSettings (context, hp) {
      context.state.profile.herdProfile.herdName = hp.herdName
      context.state.profile.herdProfile.year = hp.year
      context.state.profile.herdProfile.herdGroup = hp.herdGroup
    },

    storeAnimalSources ({ commit }, { animalSources }) {
      commit('storeAnimalSources', animalSources)
    },

    updateCurrentBullSale (context, { sale }) {
      context.state.currentBullSale = sale
    },

    updateSaleDayMode (context, { isSaleDayMode }) {
      context.state.appSettings.isSaleDayMode = isSaleDayMode
    },

    storeBullSales ({ commit }, { sales }) {
      commit('storeBullSales', sales)
    },

    storeCurrentBullSale ({ commit }, { sale }) {
      commit('storeCurrentBullSale', sale)
    },

    storeSaleDateAccounts ({ commit }, { saleDateAccounts }) {
      commit('storeSaleDateAccounts', saleDateAccounts)
    },

    loadProfile (context, { authData }) {
      const payload = authData.idTokenPayload

      // user metadata
      const userMetadata = payload['https://cattlytics.com/user_metadata'] !== undefined
        ? payload['https://cattlytics.com/user_metadata']
        : {}

      // app metadata
      const appMetadata = payload['https://cattlytics.com/app_metadata'] !== undefined
        ? payload['https://cattlytics.com/app_metadata']
        : {}

      // user stats (logins count)
      const stats = payload['https://cattlytics.com/stats'] !== undefined
        ? payload['https://cattlytics.com/stats']
        : {}

      // client metadata
      const clientMetadata = payload['https://cattlytics.com/clientMetadata'] !== undefined
        ? payload['https://cattlytics.com/clientMetadata']
        : null

      // sale day mode
      const isSaleDayMode = payload['https://cattlytics.com/isSaleDayMode'] !== undefined
        ? payload['https://cattlytics.com/isSaleDayMode']
        : false

      // build user profile
      const profile = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        nickname: payload.nickname,
        loginsCount: stats.loginsCount,
        companyName: userMetadata.companyName !== undefined
          ? userMetadata.companyName
          : '',
        phone1: userMetadata.phone1 !== undefined
          ? userMetadata.phone1
          : '',
        phone2: userMetadata.phone2 !== undefined
          ? userMetadata.phone2
          : '',
        cowHerdSize: userMetadata.cowHerdSize !== undefined
          ? userMetadata.cowHerdSize
          : null,
        bullPreferences: userMetadata.bullPreferences !== undefined
          ? userMetadata.bullPreferences
          : '',
        isBeefboosterCustomer: userMetadata.isBeefboosterCustomer !== undefined
          ? userMetadata.isBeefboosterCustomer
          : false,
        isHerdtraxCustomer: userMetadata.isHerdtraxCustomer !== undefined
          ? userMetadata.isHerdtraxCustomer
          : false,
        picture: userMetadata.picture !== undefined
          ? userMetadata.picture
          : payload.picture,
        roles: appMetadata.roles !== undefined
          ? appMetadata.roles
          : [ 'Guest' ],
        herdtraxHerds: userMetadata.herdtraxHerds !== undefined
          ? userMetadata.herdtraxHerds
          : [],
        herdProfile: {
          herdName: null,
          year: null,
          herdGroup: null
        },
        bbCustomerStatus: userMetadata.bbCustomerStatus !== undefined
          ? userMetadata.bbCustomerStatus
          : {},
        hxCustomerStatus: userMetadata.hxCustomerStatus !== undefined
          ? userMetadata.hxCustomerStatus
          : {}
      }

      // store profile
      store.state.profile = profile

      // atore appSettings
      store.state.appSettings = {
        saleDayRuleId: clientMetadata.RULE_ID_SDM,
        isSaleDayMode: isSaleDayMode        
      }

      // -------- custom restore from local storage -----------

      // animal sources
      if (localStorage.getItem('animalSources') !== undefined) {
        const animalSources = JSON.parse(localStorage.getItem('animalSources'))
        store.state.animalSources = animalSources
      }

      // bull sales
      if (localStorage.getItem('bullSales') !== undefined) {
        const bullSales = JSON.parse(localStorage.getItem('bullSales'))
        store.state.bullSales = bullSales
      }

      // pod accounts
      if (localStorage.getItem('saleDateAccounts') !== undefined) {
        const saleDateAccounts = JSON.parse(localStorage.getItem('saleDateAccounts'))
        store.state.saleDateAccounts = saleDateAccounts
      }
    }
  }
})

if (typeof window !== 'undefined') {
  document.addEventListener('DOMContentLoaded', function () {
    if (store.state.accessToken !== null) {
      auth.renewTokens(store.state.accessToken, store.state.expiresAt).then(authData => {
        store.dispatch('loadProfile', { authData: authData })
      }).catch(() => {
        store.dispatch('logout')
      })
    }
  })
}

export default store
